import {
  Component,
  OnInit,
  Input,
  Type,
  ViewChild,
  ComponentRef,
  ViewContainerRef,
  ComponentFactoryResolver,
  DoCheck,
  Output,
  EventEmitter,
  OnDestroy,
  Inject,
  forwardRef,
  ChangeDetectorRef
} from '@angular/core';
import {
  FormActivity,
  FormLayoutModes,
  SingleColumnFormLayoutModel,
  Activity,
  ActivityModel
} from '../../../../../../models/activities';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { FormActivityInputLayoutSingleColumnComponent } from '../form-activity-input-layout-single-column/form-activity-input-layout-single-column.component';
import { FormActivityInputLayoutTwoColumnComponent } from '../form-activity-input-layout-two-column/form-activity-input-layout-two-column.component';
import { FormActivityInputLayoutBaseComponent } from '../form-activity-input-layout-base/form-activity-input-layout-base.component';
import { WorkflowApplication } from '../../../../../../models';
import { Subscription, interval } from 'rxjs';
import moment from 'moment';
import { WorkflowContextService, WorkflowService } from 'src/app/services';
import { CalculatedValueDataEntity } from 'src/app/models/data-entities';

@Component({
    selector: 'wm-form-activity-input-view',
    templateUrl: './form-activity-input-view.component.html',
    styleUrls: ['./form-activity-input-view.component.css'],
    standalone: false
})
export class FormActivityInputViewComponent implements OnInit, OnDestroy {
  @Input() activity: FormActivity;
  // Embedded inspection steps render a subset of the owning activity's fields.
  // Formula requests must save the owner, including its status and iteration.
  @Input() calculationActivity: FormActivity;
  @Input() form: UntypedFormGroup;
  @Input() isReadOnly: boolean;
  @Input() applicationId: string;
  @Output() calculateActivity: EventEmitter<{
    templateCode: string;
    calculateFormula?: boolean;
    visReq?: boolean;
  }> = new EventEmitter();

  calculating = false;
  calculatingReqVis = false;
  hasChanges = false;
  hasReqVisChanges = false;
  waitingForCalculate = false;
  calcSubscription: Subscription = null;
  lastCalcRequest: Date;
  lastReqVisRequest: Date;
  entityChanges: string[] = [];
  reqVisEntityChanges: string[] = [];

  lastEntityFormControlValue: { [key: string]: string } = {};

  @ViewChild('layoutContainer', { read: ViewContainerRef, static: true })
  layoutContainer: ViewContainerRef;
  componentRef: ComponentRef<FormActivityInputLayoutBaseComponent>;

  layoutTypes: any = {
    0: {
      layoutComponent: FormActivityInputLayoutSingleColumnComponent
    },
    1: {
      layoutComponent: FormActivityInputLayoutTwoColumnComponent
    }
  };

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    @Inject(forwardRef(() => WorkflowService))
    private _workflowSvc: WorkflowService,
    @Inject(forwardRef(() => WorkflowContextService))
    private _context: WorkflowContextService,
    private _ref: ChangeDetectorRef
  ) {
    this.isReadOnly = false;
  }

  ngOnDestroy(): void {
    if (this.calcSubscription && !this.calcSubscription.closed) {
      this.calcSubscription.unsubscribe();
    }
  }

  async persistChildComponent() {
    return this.componentRef.instance.persistChildComponent();
  }

  ngOnInit() {
    if (this.activity) {
      this.loadLayoutComponent(this.activity.model.formLayout);
    }

    // check for changes every 500 ms.
    this.calcSubscription = interval(500).subscribe(() => {
      // this is here as a buffer to make sure we aren't firing this more than necessary
      const msSinceLastCalcRequest: number = moment().diff(
        moment(this.lastCalcRequest),
        'milliseconds'
      );

      // this is here as a buffer to make sure we aren't firing this more than necessary
      const msSinceLastVisReqRequest: number = moment().diff(
        moment(this.lastReqVisRequest),
        'milliseconds'
      );

      if (
        this.hasChanges &&
        !this.calculating &&
        msSinceLastCalcRequest > 500
      ) {
        this.hasChanges = false;
        const templateCodes: string[] = this.entityChanges;
        this.entityChanges = [];
        this.calculating = true;
        // persist child components for all entities on form
        this.persistChildComponent();

        let calculationActivity = this.calculationActivity || this.activity;
        if (calculationActivity.type === 'consolidated-inspection-activity') {
          // Nested steps share fields with the merged layout. Omit them from
          // the request so those fields serialize fully instead of as $refs.
          calculationActivity = Object.assign({}, calculationActivity, {
            submittedStep: null,
            model: Object.assign({}, calculationActivity.model, {
              requestStepActivity: null,
              scheduleStepActivity: null,
              completeStepActivity: null
            })
          });
        }

        this._workflowSvc
          .calculateFormulas(
            calculationActivity,
            this.applicationId || this._context.applicationId,
            templateCodes
          )
          .subscribe(
            result => {
              const entities = this.activity.model.getEntities();
              let stillCalculating = false;

              // update each of the entities with the calculated value
              entities.forEach(e => {
                const tempCodeKey = e.templateCode.toLowerCase();
                if (
                  templateCodes.indexOf(e.templateCode) > -1 &&
                  e instanceof CalculatedValueDataEntity
                ) {
                  const calcE = e as CalculatedValueDataEntity;


                  // make sure other changes weren't queued while calculating before setting it back to not calculating
                  if (
                    !this.entityChanges.find(ec => ec == calcE.templateCode)
                  ) {
                    calcE.isCalculating = false;
                  } else {
                    stillCalculating = true;
                  }

                  calcE.valueUpdate$.emit(result.calculatedValues[tempCodeKey]);
                }
                if (!stillCalculating) {
                  this.calculating = false;
                }
              });
              this._ref.detectChanges();
            },
            err => {
              this.calculating = false;
              this._ref.detectChanges();
            }
          );
      } else if (
        (this.reqVisEntityChanges || []).length > 0 &&
        !this.calculatingReqVis &&
        msSinceLastVisReqRequest > 500
      ) {
        this.hasReqVisChanges = false;

        const reqVisTemplateCodes: string[] = this.reqVisEntityChanges;
        this.reqVisEntityChanges = [];
        this.calculatingReqVis = true;
        // persist child components for all entities on form
        this.persistChildComponent();

        // calculate the conditionally visible and required client side
        // get list of entities on the form that are in the reqVisTemplateCodes

        const activityEntities = this.activity.model.getEntities();
        const reqVisEntities = this.activity.model.getEntities().filter(e => {
          return reqVisTemplateCodes.indexOf(e.templateCode) > -1;
        });

        // reset the isRequired to the original Is Required to make sure things are sent to the server correctly
        // isRequired on the local entity gets set to false if the entity is not visible but this impacts things on the server since we are changing a configured value
        reqVisEntities.forEach(e => {
          e.isRequired = e.isRequiredOriginal;
        });

        // calculate the conditionally visible and required client side
        // TODO: the select-contractor-data-entity needs to know when to remove its 'entityControls' form control when it becomes not visible.
        // There was a fix in the 2.3.64 release in which select-contractor-data-entity overrides cancelCalculation() and checks isVisible at that point,
        // but now with this refactor to client-side calculation, we need to make sure that when an entity becomes not visible, it can remove proprietary
        // form controls.
        reqVisEntities.forEach(e => {
          e.evaluateIsVisible(activityEntities, this.activity).subscribe(v => {
            e.isVisible = v;
            if (!e.isVisible) {
              this.form.controls[e.templateCode].disable();
            } else {
              this.form.controls[e.templateCode].enable();
            }
          });
          e.evaluateIsRequired(activityEntities, this.activity).subscribe(r => {
            e.isRequired = r;
            if (e.isRequired) {
              this.form.controls[e.templateCode].setValidators([
                Validators.required
              ]);
              this.form.controls[e.templateCode].updateValueAndValidity();
            } else {
              this.form.controls[e.templateCode].clearValidators();
              this.form.controls[e.templateCode].updateValueAndValidity();
            }
          });
        });

        this.calculatingReqVis = false;
      }
    });
  }

  loadLayoutComponent(layoutMethod: FormLayoutModes) {
    let compType: Type<FormActivityInputLayoutBaseComponent>;

    if (this.layoutContainer) {
      this.layoutContainer.clear();

      if (this.layoutTypes[layoutMethod]) {
        compType = this.layoutTypes[layoutMethod].layoutComponent;
      }

      if (compType && this.layoutContainer) {
        this.componentRef = this.layoutContainer.createComponent(compType);
        this.componentRef.instance.activity = this.activity;
        this.componentRef.instance.form = this.form;
        this.componentRef.instance.isReadOnly = this.isReadOnly;
        this.componentRef.instance.applicationId = this.applicationId;
        this.componentRef.instance.calculateActivity.subscribe(e => {
          if (e.calculateFormula) {
            this.hasChanges = true;
            if (!this.entityChanges.find(ec => ec == e.templateCode)) {
              this.entityChanges.push(e.templateCode);
            }
            this.lastCalcRequest = moment().toDate();
          } else if (e.visReq) {
            this.hasReqVisChanges = true;
            if (!this.reqVisEntityChanges.find(ec => ec == e.templateCode)) {
              this.reqVisEntityChanges.push(e.templateCode);
            }
            this.lastReqVisRequest = moment().toDate();
          }
          this.calculateActivity.emit(e);
        });
      }
    }
  }
}
